// hooks/useCreateBookReviewTemplate.ts
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { postAPI, patchAPI } from '../../services/api.service';

/**
 * Question types used in the template form
 */
export type QuestionType =
  | 'text'
  | 'textarea'
  | 'select'
  | 'multi'
  | 'number'
  | 'date'
  | 'boolean'
  | 'rating'
  | 'timestampedNotes';

export interface QuestionFormItem {
  qid: string;
  label: string;
  type: QuestionType;
  required?: boolean;
  placeholder?: string;
  // in the form we keep a comma-separated string for options; converted at submit
  optionsStr?: string;
  order?: number;
}

/**
 * Payload shape for creating/updating templates.
 * assignment and groupId are allowed to be included (component builds them).
 */
export interface TemplateCreatePayload {
  title: string;
  description?: string;
  tags?: string[];
  groupId?: string;
  sections: {
    pre: QuestionFormItem[];
    during: QuestionFormItem[];
    post: QuestionFormItem[];
  };
  // assignment can be present during create/update (component ensures mandatory assignment on create)
  assignment?: {
    _id?: string; // present when editing an existing assignment
    title?: string;
    assignedAt?: string | null;
    dueAt?: string | null;
    visibleFrom?: string | null;
    visibleTo?: string | null;
    allowDrafts?: boolean;
    allowDuringNotes?: boolean;
    status?: 'active' | 'closed' | 'archived';
    groupId?: string;
  };
}

/**
 * Hook now accepts an optional existingReview. If provided and has _id -> hook will PATCH
 * otherwise it will POST to create.
 *
 * Usage:
 *  const { handleCreateTemplate, loading } = useCreateBookReviewTemplate(existingReview);
 */
export const useCreateBookReviewTemplate = (existingReview?: { _id?: string } | null) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleCreateTemplate = async (payload: TemplateCreatePayload) => {
    setLoading(true);
    try {
      // get logged-in user id from localStorage (same pattern you use in other hooks)
      const user = JSON.parse(localStorage.getItem('user') || '{}') as { id?: string };
      const createdBy = user?.id;
      if (!createdBy) {
        toast.error('User not found in local storage. Please login again.');
        setLoading(false);
        return;
      }

      // ensure assignment exists on create (your frontend should provide it; double-check)
      if (!existingReview) {
        // create flow: assignment is mandatory per your note
        if (!payload.assignment || !payload.assignment.groupId) {
          toast.error('Assignment information (including group) is required when creating a template.');
          setLoading(false);
          return;
        }
      }

      // Build body:
      // - For create: include createdBy
      // - For update: do not force createdBy, send payload as-is (component includes assignment._id when needed)
      const body: any = existingReview && existingReview._id
        ? { ...payload }
        : { ...payload, createdBy };

      // NOTE: adjust endpoints below if your router mounts under a different base path.
      // I use '/book-review-templates' — change to '/book-review' if your server expects that.
      if (existingReview && existingReview._id) {
        // Update existing template (PATCH)
        const id = String(existingReview._id);
        const res = await patchAPI<{message:string}>(`/book-review/${id}`, body);
        if (res?.status === 200) {
          toast.success('Template updated successfully!');
          navigate(`${process.env.PUBLIC_URL || ''}/book_review/template_list`);
        } else {
          const msg = res?.data?.message || 'Failed to update template';
          toast.error(msg);
        }
      } else {
        // Create new template (POST)
        const res = await postAPI<{message:string}>('/book-review', body);
        if (res?.status === 201 || res?.status === 200) {
          toast.success('Template created successfully!');
          navigate(`${process.env.PUBLIC_URL || ''}/book_review/template_list`);
        } else {
          const msg = res?.data?.message || 'Failed to create template';
          toast.error(msg);
        }
      }
    } catch (err: any) {
      console.error('create/update template error', err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'An error occurred creating/updating the template';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return { handleCreateTemplate, loading };
};

export default useCreateBookReviewTemplate;
